import { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, PackageX } from "lucide-react";
import { Badge } from "../ui/badge";
import { formatCurrency } from "../../lib/utils";

const fmtQty = (q) => (q == null ? "—" : Number.isInteger(q) ? String(q) : String(q).replace(".", ","));
const money = (v) => (v == null ? "—" : formatCurrency(v));

const STATUS = {
  out: { label: "Esgotado", cls: "bg-red-500/15 text-red-600", icon: PackageX },
  low: { label: "Baixo", cls: "bg-amber-500/15 text-amber-600", icon: AlertTriangle },
  ok: { label: "OK", cls: "bg-emerald-500/15 text-emerald-600" },
  untracked: { label: "Sem controlo", cls: "bg-muted text-muted-foreground" },
};

const StockCell = ({ r }) => {
  const s = STATUS[r.stock_status];
  const Icon = s.icon;
  return (
    <div className="flex items-center justify-end gap-2" data-testid={`report-stock-${r.product_id}`}>
      <span className={`font-mono font-semibold ${r.stock_status === "out" ? "text-red-600" : r.stock_status === "low" ? "text-amber-600" : ""}`}>{r.track_stock ? fmtQty(r.current_stock) : "—"}</span>
      <Badge className={`${s.cls} text-[10px] px-1.5 gap-1`}>{Icon && <Icon className="w-3 h-3" />}{s.label}{r.track_stock && r.stock_status !== "ok" ? ` · mín ${fmtQty(r.min_stock)}` : ""}</Badge>
    </div>
  );
};

const KPI = ({ label, value, sub, testId, tone }) => (
  <div className="rounded-xl border bg-card p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-lg font-bold font-mono ${tone || ""}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export const ProductSalesKpis = ({ totals }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="report-kpis">
    <KPI label="Unidades vendidas" value={fmtQty(totals.qty)} sub={`${totals.orders} pedido(s)`} testId="report-kpi-qty" />
    <KPI label="Valor vendido" value={money(totals.revenue)} testId="report-kpi-revenue" />
    <KPI label="Custo" value={money(totals.cost)} testId="report-kpi-cost" />
    <KPI label="Margem" value={money(totals.margin)} tone={totals.margin < 0 ? "text-red-600" : "text-emerald-600"} sub={totals.revenue && totals.margin != null ? `${Math.round((totals.margin / totals.revenue) * 100)}%` : null} testId="report-kpi-margin" />
    <KPI label="Artigos vendidos" value={`${totals.sold_products} / ${totals.products}`} sub="vendidos / listados" testId="report-kpi-products" />
    <KPI label="Stock em alerta" value={String(totals.low_stock)} tone={totals.low_stock ? "text-amber-600" : ""} sub="baixo ou esgotado" testId="report-kpi-low-stock" />
  </div>
);

const CategoryGroup = ({ c }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid={`report-category-group-${c.category_id || "none"}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors" data-testid={`report-category-toggle-${c.category_id || "none"}`}>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{c.category}</p>
          <p className="text-[11px] text-muted-foreground">{c.sold_products} de {c.products} artigos vendidos{c.low_stock ? ` · ${c.low_stock} em alerta de stock` : ""}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono font-bold">{money(c.revenue)}</p>
          <p className="text-[11px] text-muted-foreground font-mono">{fmtQty(c.qty)} un. vendidas</p>
        </div>
      </button>
      {open && (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Produto</th>
                <th className="text-right px-2 py-2 font-medium whitespace-nowrap">Vendido</th>
                <th className="text-right px-2 py-2 font-medium whitespace-nowrap">Valor</th>
                <th className="text-right px-2 py-2 font-medium whitespace-nowrap hidden md:table-cell">Custo</th>
                <th className="text-right px-2 py-2 font-medium whitespace-nowrap hidden md:table-cell">Margem</th>
                <th className="text-right px-4 py-2 font-medium whitespace-nowrap">Stock no sistema</th>
              </tr>
            </thead>
            <tbody>
              {c.items.map((r) => (
                <tr key={r.product_id || r.name} className={`border-t ${r.qty_sold ? "" : "text-muted-foreground"}`} data-testid={`report-row-${r.product_id || "noid"}`}>
                  <td className="px-4 py-2">
                    <p className="font-medium text-foreground">{r.name}{r.inactive && <span className="ml-1 text-[10px] text-muted-foreground">(inactivo)</span>}</p>
                    {(r.barcode || r.sku) && <p className="text-[10px] font-mono text-muted-foreground">{r.barcode || r.sku}</p>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono font-semibold whitespace-nowrap" data-testid={`report-qty-${r.product_id}`}>{fmtQty(r.qty_sold)} <span className="text-[10px] font-normal text-muted-foreground">{r.unit}</span></td>
                  <td className="px-2 py-2 text-right font-mono whitespace-nowrap">{money(r.revenue)}</td>
                  <td className="px-2 py-2 text-right font-mono whitespace-nowrap hidden md:table-cell">{money(r.cost)}</td>
                  <td className={`px-2 py-2 text-right font-mono whitespace-nowrap hidden md:table-cell ${r.margin < 0 ? "text-red-600" : ""}`}>{money(r.margin)}{r.margin_pct != null && r.qty_sold ? <span className="text-[10px] text-muted-foreground"> {r.margin_pct}%</span> : null}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap"><StockCell r={r} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30 font-semibold">
                <td className="px-4 py-2">Total {c.category}</td>
                <td className="px-2 py-2 text-right font-mono">{fmtQty(c.qty)}</td>
                <td className="px-2 py-2 text-right font-mono">{money(c.revenue)}</td>
                <td className="px-2 py-2 text-right font-mono hidden md:table-cell">{money(c.cost)}</td>
                <td className="px-2 py-2 text-right font-mono hidden md:table-cell">{money(c.margin)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export const ProductSalesReport = ({ report }) => {
  if (!report) return null;
  if (!report.categories.length) {
    return <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="report-empty">Sem produtos para mostrar neste período.</div>;
  }
  return (
    <div className="space-y-3" data-testid="report-categories">
      {report.categories.map((c) => <CategoryGroup key={c.category_id || c.category} c={c} />)}
    </div>
  );
};
